import { useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { SearchableModule, useInfiniteGlobalSearchModule } from '@sib/api';
import type { GlobalSearchItemDto } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { cn } from '@sib/ui';
import { ChevronDown, LoaderCircle } from '@sib/ui/icons';
import { GlobalSearchResultItem } from './GlobalSearchResultItem';
import { GLOBAL_SEARCH_MODULE_LIST_CLASS_NAME } from '../GlobalSearch.constants';
import {
  globalSearchModuleHeaderClassName,
  globalSearchModuleHeaderRowClassName,
} from '../GlobalSearch.styles';
import {
  getGlobalSearchModuleItems,
  useGlobalSearchModuleLabels,
} from '../GlobalSearch.utils';

interface GlobalSearchModuleSectionProps {
  module: SearchableModule;
  searchTerm: string;
  totalItems: number;
  listClassName?: string;
  defaultExpanded?: boolean;
  onItemSelect: (item: GlobalSearchItemDto) => void;
}

export const GlobalSearchModuleSection = ({
  module,
  searchTerm,
  totalItems,
  listClassName = GLOBAL_SEARCH_MODULE_LIST_CLASS_NAME.compact,
  defaultExpanded = true,
  onItemSelect,
}: GlobalSearchModuleSectionProps) => {
  const { t } = useTranslation();
  const { getModuleLabel } = useGlobalSearchModuleLabels();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const loadingLabel = t('components.topbar.search.loading');

  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteGlobalSearchModule({
    module,
    query: { searchTerm },
    options: {
      enabled: isExpanded,
    },
  });

  const items = useMemo(() => getGlobalSearchModuleItems(data), [data]);
  const resolvedTotalItems = data?.pages[0]?.totalItems ?? totalItems;

  const [sentryRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    onLoadMore: () => !isFetchingNextPage && fetchNextPage(),
    disabled: isError || !isExpanded,
    rootMargin: '0px 0px 120px 0px',
  });

  return (
    <section className="w-full">
      <button
        type="button"
        className={cn(globalSearchModuleHeaderRowClassName, 'w-full text-left')}
        onClick={() => setIsExpanded(expanded => !expanded)}
        aria-expanded={isExpanded}
      >
        <h3 className={globalSearchModuleHeaderClassName}>
          {t('components.topbar.search.moduleSection', {
            module: getModuleLabel(module),
            count: resolvedTotalItems,
          })}
        </h3>
        <ChevronDown
          className={cn(
            'text-primary size-4 shrink-0 transition-transform',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {isExpanded && (
        <div
          className={cn(
            'custom-scrollbar-muted overflow-y-auto',
            listClassName
          )}
        >
          {isLoading && items.length === 0 ? (
            <div className="text-primary/55 flex items-center justify-center gap-2 px-4 py-3 text-sm">
              <LoaderCircle className="size-4 animate-spin" />
              {loadingLabel}
            </div>
          ) : (
            <ul>
              {items.map(item => (
                <li key={`${item.module}-${item.id}`}>
                  <GlobalSearchResultItem
                    item={item}
                    searchTerm={searchTerm}
                    onSelect={onItemSelect}
                  />
                </li>
              ))}
            </ul>
          )}

          {(hasNextPage || isFetchingNextPage) && (
            <div
              ref={sentryRef}
              className="text-primary/55 flex items-center justify-center gap-2 py-2 text-sm"
            >
              <LoaderCircle className="size-4 animate-spin" />
              {loadingLabel}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
